'use client';

import { useEffect, useMemo, useState, type CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useBlockPart,
  useMarketplaceSource,
  useCategoryTreeSource,
  useLocale,
  type MarketplaceCatalogCard as SourceCatalogCard,
} from '@akademi/site-builder-runtime';
import { Input, SelectField } from '@akademi/ui';
import { Search } from 'lucide-react';
import { SectionShell } from '../_shared/SectionShell';
import { type PaddingBox } from '../_shared/resolvePadding';
import { EmptyState } from '../_shared/EmptyState';
import { SkeletonRow } from '../_shared/SkeletonRow';
import { asList, tx } from '../_shared/coerce';
import { textTertiary } from '../_shared/styles';
import { MarketplaceCourseCard } from '../cards/MarketplaceCourseCard';
import { type MarketPrice } from './_shared';
import { MarketplaceCard, toCourseCardProps, type MarketplaceCardData } from './_MarketplaceCard';
import { MarketplaceFacets, type MarketplaceFacetGroup } from './_MarketplaceFacets';
import { useMarketplaceCart } from './_useMarketplaceCart';

type Localized = string | Record<string, string>;

export interface MarketplaceCatalogItem {
  id?: string;
  image?: string;
  title?: Localized;
  description?: Localized;
  category?: string;
  /** Overrides the "Program" chip label on the card image. */
  kindLabel?: string;
  kindIcon?: 'elearning' | 'program' | 'classroom' | 'video';
  /** Present only for a genuine multi-course bundle — absence is what
   *  marks an item as a standalone course (see {@link toCard}). */
  courseCount?: number;
  price?: MarketPrice;
  free?: boolean;
  href?: string;
  // Standalone-course-only fields — mirrors MarketplaceCourseItem so a
  // course surfaced through this top-level listing renders with the same
  // fidelity as one reached via MarketplaceCourseGrid.
  provider?: string;
  providerLogo?: string;
  level?: string;
  attendeeCount?: number;
  likeCount?: number;
  dislikeCount?: number;
  startDate?: string;
  endDate?: string;
}

export interface MarketplaceCatalogGridProps {
  title?: Localized;
  description?: Localized;
  items?: MarketplaceCatalogItem[];
  emptyTitle?: Localized;
  showSearch?: boolean;
  showFilters?: boolean;
  showSort?: boolean;
  columns?: '2' | '3' | '4';
  /** Which card design to render for each item — the simple akademi-sem tile
   *  (default) or the richer `marketplace` registry card variant. */
  cardStyle?: 'simple' | 'marketplace';
  paddingOuter?: PaddingBox;
  paddingInner?: PaddingBox;
}

const DEFAULT_ITEMS: MarketplaceCatalogItem[] = [
  {
    id: 'leadership',
    title: 'Leadership Essentials',
    description: 'A 6-course bundle covering coaching, feedback and delegation for new managers.',
    category: 'Leadership',
    courseCount: 6,
    price: { amount: '1499.00', currency: 'TRY' },
  },
  {
    id: 'data-literacy',
    title: 'Data Literacy for Everyone',
    description: 'From spreadsheets to dashboards — reading and questioning data at work.',
    category: 'Data',
    courseCount: 4,
    free: true,
  },
  {
    id: 'onboarding',
    title: 'New Hire Onboarding',
    description: 'Everything a new starter needs in their first 30 days.',
    category: 'HR',
    courseCount: 8,
    price: { amount: '990.00', currency: 'TRY' },
  },
];

const COURSE_TYPE_I18N_KEYS: Record<string, string> = {
  F2F_COURSE: 'f2fCourse',
  REMOTE_COURSE: 'remoteCourse',
  PROGRAM: 'program',
};
const COURSE_LEVEL_VALUES = ['PUBLIC', 'BEGINNER', 'INTERMEDIATE', 'ADVANCED'] as const;

const GRID_CSS = `
.mp-catalog-layout {
  display: grid;
  grid-template-columns: 260px minmax(0, 1fr);
  gap: var(--space-6, 24px);
  align-items: start;
}
.mp-catalog-layout.no-rail { grid-template-columns: 1fr; }
.mp-catalog-cards {
  display: grid;
  grid-template-columns: repeat(var(--mp-cols, 3), minmax(0, 1fr));
  gap: var(--space-5, 20px);
}
@media (max-width: 1100px) { .mp-catalog-cards { --mp-cols: 2 !important; } }
@media (max-width: 960px) {
  .mp-catalog-layout { grid-template-columns: 1fr; }
}
@media (max-width: 560px) { .mp-catalog-cards { --mp-cols: 1 !important; } }
`;

function fromSource(card: SourceCatalogCard): MarketplaceCatalogItem {
  return {
    id: card.id,
    image: card.thumbnailUrl,
    title: card.title,
    description: card.description,
    kindLabel: card.kindLabel,
    kindIcon: card.kindIcon,
    courseCount: card.courseCount,
    price: card.fromPrice,
    free: card.freeItem,
    href: card.publicPath,
    provider: card.provider,
    providerLogo: card.providerLogo,
    level: card.level,
    attendeeCount: card.attendeeCount,
    likeCount: card.likeCount,
    dislikeCount: card.dislikeCount,
    startDate: card.startDate,
    endDate: card.endDate,
  };
}

function toCard(item: MarketplaceCatalogItem): MarketplaceCardData {
  // A genuine bundle has no attendee/like/date data of its own — it's the
  // absence of `courseCount` that marks an item as a standalone course
  // (see MarketplaceCatalogItem's docs), so default the chip to "catalog"
  // only when there's nothing course-ish to show instead.
  return {
    id: item.id,
    image: item.image,
    title: item.title,
    category: item.category,
    kindIcon: item.kindIcon ?? (item.courseCount !== undefined ? 'catalog' : undefined),
    kindLabel: item.kindLabel,
    courseCount: item.courseCount,
    price: item.price,
    free: item.free,
    href: item.href,
    provider: item.provider,
    providerLogo: item.providerLogo,
    level: item.level,
    attendeeCount: item.attendeeCount,
    likeCount: item.likeCount,
    dislikeCount: item.dislikeCount,
    startDate: item.startDate,
    endDate: item.endDate,
  };
}

/**
 * Catalog/bundle listing — the akademi.ws `/catalog` layout (a left filter
 * rail, a header with search + sort, and a responsive card grid of
 * akademi-sem-style tiles). With a {@link useMarketplaceSource} mounted it
 * lists real catalogs (`POST /marketplace/catalog/search`) and add-to-cart
 * hits the live `/marketplace/cart`; with no source it filters the authored
 * `items` client-side — the "fallbacks stay, for testing" contract.
 */
export function MarketplaceCatalogGrid({
  title,
  description,
  items,
  emptyTitle,
  showSearch = true,
  showFilters = true,
  showSort = true,
  columns = '3',
  cardStyle = 'simple',
  paddingOuter,
  paddingInner,
}: MarketplaceCatalogGridProps) {
  const { t } = useTranslation('site-builder-blocks');
  const part = useBlockPart();
  const source = useMarketplaceSource();
  const cart = useMarketplaceCart();
  const { locale } = useLocale();

  const categoryTreeSource = useCategoryTreeSource();

  const [fetched, setFetched] = useState<MarketplaceCatalogItem[] | null>(null);
  const [status, setStatus] = useState<'idle' | 'loading' | 'error'>('idle');
  const [courseCategories, setCourseCategories] = useState<{ id: string; name: string }[]>([]);

  const [query, setQuery] = useState('');
  const [sort, setSort] = useState('relevance');
  const [selected, setSelected] = useState<Record<string, Set<string>>>({});
  const [localCartIds, setLocalCartIds] = useState<Set<string>>(new Set());

  // The course-category taxonomy for the "Kategoriler" facet — same tree
  // `CategoryTree` with `feedSource: "course"` renders, fetched once.
  useEffect(() => {
    if (!categoryTreeSource) return;
    let cancelled = false;
    categoryTreeSource.getTree('course').then((tree) => {
      if (cancelled) return;
      setCourseCategories(tree.map((n) => ({ id: String(n.id), name: n.name })));
    });
    return () => {
      cancelled = true;
    };
  }, [categoryTreeSource]);

  // With a source mounted, category / channel / level / price are real
  // server-side facets — refetch `listCatalogs` whenever any of them (or the
  // search box) changes, passing them straight through as the backend's
  // `catalog/search` filter fields.
  useEffect(() => {
    if (!source) return;
    let cancelled = false;
    setStatus('loading');
    // Category ids come from the CMS category tree as number | string —
    // send them back the same shape the backend gave us (most are numeric).
    const categoryIds = Array.from(selected.category ?? []).map((v) =>
      /^\d+$/.test(v) ? Number(v) : v,
    );
    const courseTypes = Array.from(selected.channel ?? []);
    const courseLevels = Array.from(selected.level ?? []);
    const priceSel = selected.price;
    const marketplaceFreeItems = priceSel && priceSel.size === 1 ? priceSel.has('free') : undefined;
    source
      .listCatalogs({
        searchTerm: query.trim() || undefined,
        categoryIds: categoryIds.length > 0 ? categoryIds : undefined,
        courseTypes: courseTypes.length > 0 ? courseTypes : undefined,
        courseLevels: courseLevels.length > 0 ? courseLevels : undefined,
        marketplaceFreeItems,
      })
      .then((listing) => {
        if (cancelled) return;
        setFetched(listing.items.map(fromSource));
        setStatus('idle');
      })
      .catch(() => {
        if (!cancelled) setStatus('error');
      });
    return () => {
      cancelled = true;
    };
  }, [source, query, selected]);

  const raw = asList<MarketplaceCatalogItem>(items);
  const all = source ? (fetched ?? []) : raw.length > 0 ? raw : DEFAULT_ITEMS;

  const isInCart = (id: string) => (cart ? cart.inCart(id) : localCartIds.has(id));
  const toggle = (id: string) => {
    if (cart) {
      if (cart.inCart(id)) void cart.removeByItemId(id).catch(() => {});
      else void cart.add(id).catch(() => {});
      return;
    }
    setLocalCartIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleFacet = (groupKey: string, value: string) =>
    setSelected((prev) => {
      const next = { ...prev };
      const set = new Set(next[groupKey] ?? []);
      if (set.has(value)) set.delete(value);
      else set.add(value);
      next[groupKey] = set;
      return next;
    });

  const localCategories = useMemo(
    () => Array.from(new Set(all.map((i) => i.category).filter((c): c is string => Boolean(c)))),
    [all],
  );

  const priceGroup: MarketplaceFacetGroup = {
    key: 'price',
    label: t('marketplace.courseGrid.priceLabel', { defaultValue: 'Price' }),
    options: [
      { value: 'free', label: t('marketplace.courseGrid.free') },
      { value: 'paid', label: t('marketplace.courseGrid.paidLabel', { defaultValue: 'Paid' }) },
    ],
  };

  const facetGroups: MarketplaceFacetGroup[] = useMemo(() => {
    if (source) {
      const groups: MarketplaceFacetGroup[] = [];
      if (courseCategories.length > 0) {
        groups.push({
          key: 'category',
          label: t('marketplace.courseGrid.categoryLabel', { defaultValue: 'Categories' }),
          options: courseCategories.map((c) => ({ value: c.id, label: c.name })),
        });
      }
      groups.push({
        key: 'channel',
        label: t('marketplace.courseGrid.channelLabel', { defaultValue: 'Learning Channels' }),
        options: Object.entries(COURSE_TYPE_I18N_KEYS).map(([value, i18nKey]) => ({
          value,
          label: t(`marketplace.courseGrid.channel.${i18nKey}`),
        })),
      });
      groups.push({
        key: 'level',
        label: t('marketplace.courseGrid.levelLabel', { defaultValue: 'Course Level' }),
        options: COURSE_LEVEL_VALUES.map((v) => ({
          value: v,
          label: t(`marketplace.courseGrid.level.${v.toLowerCase()}`),
        })),
      });
      groups.push(priceGroup);
      return groups;
    }
    const groups: MarketplaceFacetGroup[] = [];
    if (localCategories.length > 0) {
      groups.push({
        key: 'category',
        label: t('marketplace.courseGrid.categoryLabel', { defaultValue: 'Categories' }),
        options: localCategories.map((c) => ({ value: c, label: c })),
      });
    }
    groups.push(priceGroup);
    return groups;
  }, [source, courseCategories, localCategories, t]);

  const filtered = useMemo(() => {
    // With a source mounted, `all` already reflects the server-side
    // search/category/channel/level/price filters — just apply the
    // client-only title sort on top.
    if (source) {
      if (sort === 'title') {
        return [...all].sort((a, b) => tx(a.title).localeCompare(tx(b.title)));
      }
      return all;
    }
    const q = query.trim().toLowerCase();
    const catSel = selected.category;
    const priceSel = selected.price;
    let out = all.filter((item) => {
      if (q) {
        const hay = `${tx(item.title)} ${tx(item.description)}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      if (catSel && catSel.size > 0 && !(item.category && catSel.has(item.category))) return false;
      if (priceSel && priceSel.size > 0) {
        const isFree = Boolean(item.free);
        if (isFree && !priceSel.has('free')) return false;
        if (!isFree && !priceSel.has('paid')) return false;
      }
      return true;
    });
    if (sort === 'title') {
      out = [...out].sort((a, b) => tx(a.title).localeCompare(tx(b.title)));
    }
    return out;
  }, [source, all, query, selected, sort]);

  const showRail = showFilters && facetGroups.length > 0;

  let body: React.ReactNode;
  if (source && status === 'loading' && fetched === null) {
    body = <SkeletonRow count={6} layout="grid" minCardWidth={240} />;
  } else if (source && status === 'error') {
    body = (
      <EmptyState
        title={t('marketplace.courseGrid.loadError.title', {
          defaultValue: "Couldn't load the catalog.",
        })}
        hint={t('marketplace.courseGrid.loadError.hint', { defaultValue: 'Please try again.' })}
      />
    );
  } else if (filtered.length === 0) {
    body = (
      <EmptyState
        title={tx(emptyTitle) || t('marketplace.courseGrid.empty.title')}
        hint={t('marketplace.courseGrid.empty.hint')}
      />
    );
  } else {
    body = (
      <div
        {...part('list')}
        className="mp-catalog-cards"
        style={{ '--mp-cols': columns } as CSSProperties}
      >
        {filtered.map((item, i) => {
          const key = item.id ?? String(i);
          return (
            <div key={key} {...part('item', i)}>
              {cardStyle === 'marketplace' ? (
                <MarketplaceCourseCard course={toCourseCardProps(toCard(item), locale)} />
              ) : (
                <MarketplaceCard
                  item={toCard(item)}
                  inCart={isInCart(key)}
                  onToggleCart={() => toggle(key)}
                  starSide={i % 2 === 0 ? 'left' : 'right'}
                />
              )}
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <SectionShell
      title={tx(title)}
      subtitle={tx(description)}
      paddingOuter={paddingOuter}
      paddingInner={paddingInner}
      maxWidth="full"
    >
      <style dangerouslySetInnerHTML={{ __html: GRID_CSS }} />

      {(showSearch || showSort) && (
        <div
          {...part('filter')}
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 'var(--space-3, 12px)',
            marginBottom: 'var(--space-4, 16px)',
          }}
        >
          <span style={{ ...textTertiary, fontSize: 'var(--font-size-sm, 13px)' }}>
            {t('marketplace.courseGrid.resultCount', { count: filtered.length })}
          </span>
          <span style={{ flex: 1 }} />
          {showSearch && (
            <div style={{ position: 'relative', flex: '0 1 280px', minWidth: 180 }}>
              <Search
                size={16}
                style={{
                  position: 'absolute',
                  left: 10,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: 'var(--color-text-tertiary, #71717a)',
                }}
              />
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={t('marketplace.courseGrid.searchPlaceholder')}
                aria-label={t('marketplace.courseGrid.searchPlaceholder')}
                style={{ paddingLeft: 32 }}
              />
            </div>
          )}
          {showSort && (
            <SelectField
              value={sort}
              onChange={setSort}
              options={[
                {
                  value: 'relevance',
                  label: t('marketplace.courseGrid.sortRelevance', {
                    defaultValue: 'Most relevant',
                  }),
                },
                {
                  value: 'title',
                  label: t('marketplace.courseGrid.sortTitle', { defaultValue: 'Title A–Z' }),
                },
              ]}
              ariaLabel={t('marketplace.courseGrid.sortLabel', { defaultValue: 'Sort by' })}
            />
          )}
        </div>
      )}

      <div className={showRail ? 'mp-catalog-layout' : 'mp-catalog-layout no-rail'}>
        {showRail && (
          <MarketplaceFacets groups={facetGroups} selected={selected} onToggle={toggleFacet} />
        )}
        <div style={{ minWidth: 0 }}>{body}</div>
      </div>
    </SectionShell>
  );
}
